import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from '../context/AuthContext';
import { CompanyProvider } from '../context/CompanyContext';
import { RoleProvider } from '../context/RoleContext';
import { LeadsProvider } from '../context/LeadsContext';
import { SettingsProvider } from '../context/SettingsContext';
import { TasksProvider } from '../context/TasksContext';
import { AutomationProvider } from '../context/AutomationContext';
import { AudienceProvider } from '../context/AudienceContext';
import { CampaignsProvider } from '../context/CampaignsContext';
import AppLayout from '../layout/AppLayout';
import AppReadyGate from './AppReadyGate';

/**
 * Shared authenticated shell.
 *
 * Sprint 286 (M1): switched from per-route `<ProtectedRoute>{children}</...>`
 * wrapping to a single layout route consumed via React Router `<Outlet />`.
 *
 *   <Route element={<ProtectedRoute />}>
 *     <Route path="/dashboard" element={<Dashboard />} />
 *     <Route path="/jobs"      element={<Jobs />} />
 *     ...
 *   </Route>
 *
 * Because the parent Route stays matched across every child navigation,
 * AuthProvider / CompanyProvider / RoleProvider / TasksProvider /
 * LeadsProvider / all downstream Firestore-heavy providers stay mounted
 * for the entire session — normal tab-to-tab clicks no longer force a
 * full app bootstrap.
 *
 * Provider state STILL fully resets when appropriate:
 *   • Logout        → AuthProvider clears `user` → CompanyProvider drops
 *                     companyId → RoleProvider clears role → downstream
 *                     providers unmount their Firestore listeners.
 *   • User change   → onAuthStateChanged fires with a new uid → same
 *                     cascade above hydrates for the new user.
 *   • Company change → CompanyProvider derives from profile.companyId;
 *                     when it changes RoleProvider re-reads membership
 *                     for the new (uid, companyId) pair.
 *
 * The `bare` variant is used for /onboarding — full-screen shell without
 * AppLayout or the Firestore-heavy provider tree.
 */
const ProtectedRouteInner = ({ bare = false }) => {
  const { user, isAuthLoading } = useAuth();

  if (isAuthLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/30">
        <div className="text-center space-y-4">
          <div className="h-12 w-12 border-4 border-[#71DED6] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-lg font-medium text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (bare) {
    return <Outlet />;
  }

  // Provider order matters — RoleProvider now sits ABOVE AppReadyGate so
  // both consume the same hydrated membership state (M6 dedup). See
  // AppReadyGate for the readiness dependencies.
  return (
    <RoleProvider>
      <AppReadyGate>
        <TasksProvider>
          <AutomationProvider>
            <LeadsProvider>
              <SettingsProvider>
                <AudienceProvider>
                  <CampaignsProvider>
                    <AppLayout>
                      <Outlet />
                    </AppLayout>
                  </CampaignsProvider>
                </AudienceProvider>
              </SettingsProvider>
            </LeadsProvider>
          </AutomationProvider>
        </TasksProvider>
      </AppReadyGate>
    </RoleProvider>
  );
};

const ProtectedRoute = ({ bare = false }) => {
  return (
    <AuthProvider>
      <CompanyProvider>
        <ProtectedRouteInner bare={bare} />
      </CompanyProvider>
    </AuthProvider>
  );
};

export default ProtectedRoute;
